import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, signal} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RealTimeNavigationService } from '../../services/real-time-navigation.service';
import { MapService } from '../../services/map.service';
import { RouteDataService } from '../../services/route-data.service';
import { ItemStatus } from '../../interfaces/model';
import { Point } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { DebugService } from 'src/app/services/debug.service';

@Component({
  selector: 'dev-panel-map',
  standalone: true,
  imports: [ 
    CommonModule, 
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './dev-panel.component.html',
  styleUrls: ['./dev-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})

export class DevPanelComponent {
    private _debugService = inject(DebugService);
    private _mapService: MapService = inject(MapService);
    private _routeDataService: RouteDataService = inject(RouteDataService);
    private _realTimeNavigationService: RealTimeNavigationService = inject(RealTimeNavigationService);
    private cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    public runningSegment = computed<Point>(() => this._routeDataService.getCurrentRouteDay()?.find(poly => poly.status === ItemStatus.RUNNING))
    public isFakeGPSActive = computed<boolean>(() => this._realTimeNavigationService.isFakeGPSActive());
    public canSetMarkerFromMap = computed<boolean>(() => this._realTimeNavigationService.canSetMarkerFromMap());

    public isRealHour = computed<boolean>(() => this._realTimeNavigationService.isRealHour());
    public manualHour = computed<string>(() => this._realTimeNavigationService.manualHour().format('HH:mm'));

    public devMode = signal<boolean>(false);
    public animationStarted = signal<boolean>(false);

    private sampledPoints: google.maps.LatLngLiteral[] = [];
    private duration: number = 60000;
    public animationRequestId: number | null = null;
    private currentIndex: number = 0;

    toggleGPSTrack(event: Event): void {
        this._realTimeNavigationService.isFakeGPSActive.update(prev => !prev);
        this._routeDataService.resetMarkersStatus();
    }

    toggleRealHour(): void {
        this._realTimeNavigationService.isRealHour.update(prev => !prev);
    }

    increaseTime(): void {
        this._realTimeNavigationService.canSetMarkerFromMap.set(false);
        this._realTimeNavigationService.manualHour.update(prev => prev.add(15, 'minute'))
        this._mapService.resetMarkerProjection()
    }

    decreaseTime(): void {
        this._realTimeNavigationService.canSetMarkerFromMap.set(false);
        this._realTimeNavigationService.manualHour.update(prev => prev.subtract(15, 'minute'))
        this._mapService.resetMarkerProjection()
    }

    focusin(evt: Event): void {
        evt.stopPropagation();
    }

    toggleDevMode(): void {
        this.devMode.update(prev => !prev);
    }

    toggleAllowAddMarker(): void {
        this._realTimeNavigationService.canSetMarkerFromMap.update(prev => !prev);
    }

    startSimulateStep(): void {
        if (!this.runningSegment())
            return;
        
        this.animationStarted.set(true);
        this.precomputeSampledPoints(this.runningSegment().polylinesPath);
        this.startAnimation();
    }

    private precomputeSampledPoints(path: google.maps.LatLngLiteral[]): void {
        const frames = Math.max(1, Math.round((this.duration / 1000) * 60));
        const latLngPath: google.maps.LatLng[] = path.map(
            (pt) => new google.maps.LatLng(pt.lat, pt.lng)
        );

        const totalLength: number = google.maps.geometry.spherical.computeLength(latLngPath);
        this.sampledPoints = [];
        for (let i = 0; i <= frames; i++) {
            const fractionAlong = i / frames; 
            const targetDist = fractionAlong * totalLength;
            let accumulated = 0;
            let pointLatLng: google.maps.LatLng = latLngPath[0]; 
            for (let j = 0; j < latLngPath.length - 1; j++) {
                const A = latLngPath[j];
                const B = latLngPath[j + 1];
                const segLen = google.maps.geometry.spherical.computeDistanceBetween(A, B);

                if (accumulated + segLen >= targetDist) {
                    const remain = targetDist - accumulated;        
                    const fracInSegment = remain / segLen;
                    pointLatLng = google.maps.geometry.spherical.interpolate(A, B, fracInSegment);
                    break;
                }
                accumulated += segLen;
            }

            this.sampledPoints.push({
                lat: pointLatLng.lat(),
                lng: pointLatLng.lng()
            });
        }
    }

    private startAnimation(): void {
        this.currentIndex = 0;
        const totalFrames: number = this.sampledPoints.length;

        const animateStep = () => {
            if (this.currentIndex >= totalFrames) {
                this.animationStarted.set(false);
                this.animationRequestId = null;
                this.cdRef.detectChanges();
                return;
            }
            const nextPos: google.maps.LatLngLiteral = this.sampledPoints[this.currentIndex];   
            this._realTimeNavigationService.setLocationMarkerOnCurrentSegment(nextPos);
            this.currentIndex++;
            this.animationRequestId = requestAnimationFrame(animateStep);
        };

        this._mapService.map.googleMap.panTo(this.sampledPoints[0]);
        this._mapService.map.googleMap.setZoom(18);
        google.maps.event.addListenerOnce(this._mapService.map.googleMap, 'idle', () => {
            this.animationRequestId = requestAnimationFrame(animateStep);
            this.cdRef.detectChanges();
        });
    }

    stopSimulateStep(): void {
        if (this.animationRequestId !== null) {
            this.animationStarted.set(false);
            cancelAnimationFrame(this.animationRequestId);
            this.animationRequestId = null;
            this._debugService.info('Animación detenida manualmente', '[DevPanelComponent]');
        }
    }

}